import TextField, { TextFieldVariants } from "@mui/material/TextField";
import { ComponentProps } from "../types";
import { CSSProperties, useMemo, useContext, useState, useEffect } from "react";
import DashboardPageContext from "../DashboardPageContext";
import { InputProps } from "@mui/material";
import ExecutionTypeContext, { Mode } from "../ExecutionTypeContext";

function Component({ component, evaluteScript }: ComponentProps) {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { context, params, updateContext } = useContext(DashboardPageContext);
	const [initValue, setInitValue] = useState<string>("");
	const [value, setValue] = useState<string>("");
	const exeMode = useContext(ExecutionTypeContext);

	useEffect(() => {
		const defaultValue = (evaluteScript(component.properties["binding"]) as string) || "";
		setValue(defaultValue);
		setInitValue(defaultValue);
	}, [component, context, evaluteScript]);

	const style = useMemo(() => {
		const style = {} as CSSProperties;
		if (exeMode?.mode === Mode.DESIGN && component.id === exeMode?.activeComponent?.id) style.border = "1px dashed red";
		if (component.properties["padding"]) style.padding = component.properties["padding"] + "px";
		if (component.properties["minWidth"]) style.minWidth = component.properties["minWidth"];
		if (component.properties["maxWidth"]) style.maxWidth = component.properties["maxWidth"];
		return style;
	}, [component, exeMode]);

	const args = useMemo(() => {
		const args = {} as { [key: string]: unknown };
		args["variant"] = (component.properties["variant"] || undefined) as TextFieldVariants;
		if (component.properties["label"]) args["label"] = component.properties["label"];
		if (component.properties["placeholder"]) args["placeholder"] = component.properties["placeholder"];
		if (component.properties["focused"] && component.properties["focused"] !== "Initial") {
			args["focused"] = component.properties["focused"] === "True" ? true : false;
		}
		if (component.properties["fullWidth"] && component.properties["fullWidth"] !== "Initial") {
			args["fullWidth"] = component.properties["fullWidth"] === "True" ? true : false;
		}
		args["size"] = component.properties["size"] || "medium";

		const inputProps = {} as InputProps;
		const inputPropsStyle = {} as CSSProperties;
		inputProps.style = inputPropsStyle;
		if (component.properties["fontSize"]) {
			inputPropsStyle.fontSize = component.properties["fontSize"] as string;
		}
		if (component.properties["fontFamily"]) {
			inputPropsStyle.fontFamily = component.properties["fontFamily"] as string;
		}
		args["inputProps"] = inputProps;
		if (component.properties["maxLines"] && parseInt(component.properties["maxLines"]) > 1) {
			args["multiline"] = true;
			args["minRows"] = parseInt(component.properties["maxLines"]);
			args["maxRows"] = parseInt(component.properties["maxLines"]);
		}
		return args;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [component, context]);

	const locatorArgs = useMemo(() => {
		const args = {} as { [key: string]: unknown };
		if (exeMode?.mode === Mode.DESIGN && exeMode.locatorActivated === true) {
			args["onClick"] = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
				if (exeMode.updateActiveComponent) exeMode.updateActiveComponent(component);
				event.stopPropagation();
			};
		}
		return args;
	}, [component, exeMode]);
	
	return (
		<TextField
			key={component.id}
			style={style}
			{...args}
			value={value}
			onChange={(event) => setValue(event.target.value)}
			onBlur={() => {
				if (initValue !== value) {
					const script = component.properties["binding"] + "=" + "`" + value.replace(/`/g, "\\`") + "`";
					evaluteScript(script);
					updateContext(context);
				}
			}}
			{...locatorArgs}
		/>
	);
}

export default Component;
